import { buildSaitamaCsvIdentity, sameSaitamaCsvIdentity } from "./saitamaCsvSelection";
import { SAITAMA_INSPECTION_CODES } from "../../config/SaitamaInspectionItems";

export const SAITAMA_CSV_GAME_DB_BATCH_SIZE = 1000;
export const SAITAMA_CSV_RESULT_CHUNK_SIZE = 500;

export const chunkSaitamaCsvResultIds = (ids = [], chunkSize = SAITAMA_CSV_RESULT_CHUNK_SIZE) => {
  const chunks = [];
  for (let index = 0; index < ids.length; index += chunkSize) {
    chunks.push(ids.slice(index, index + chunkSize));
  }
  return chunks;
};

export const getSaitamaCsvIdentityKey = (row) => {
  const identity = buildSaitamaCsvIdentity(row);
  return identity ? `${identity.game_space_id}:${identity.photograph_event_id}` : null;
};

export const dedupeSaitamaCsvRows = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = getSaitamaCsvIdentityKey(row);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const normalizeSaitamaInspectionRows = (rows = []) => {
  const byIdentity = new Map();
  rows.forEach((row) => {
    const identity = buildSaitamaCsvIdentity(row);
    if (!identity) return;
    const key = getSaitamaCsvIdentityKey(identity);
    if (!byIdentity.has(key)) {
      byIdentity.set(key, {
        ...identity,
        id: row.id ?? null,
        itemCodes: [],
      });
    }
    const itemCode = row.inspection_item_code;
    if (itemCode && !byIdentity.get(key).itemCodes.includes(itemCode)) {
      byIdentity.get(key).itemCodes.push(itemCode);
    }
  });
  byIdentity.forEach((inspection) => {
    inspection.itemCodes.sort((left, right) =>
      SAITAMA_INSPECTION_CODES.indexOf(left) - SAITAMA_INSPECTION_CODES.indexOf(right)
    );
  });
  return byIdentity;
};

export const classifySaitamaInspection = (inspection) => {
  if (!inspection || inspection.itemCodes.length === 0) return "UNINSPECTED";
  const hasNormal = inspection.itemCodes.includes("NO_ABNORMALITY");
  const hasAbnormal = inspection.itemCodes.some((code) => code !== "NO_ABNORMALITY");
  if (hasNormal && hasAbnormal) return "INVALID_MIXED";
  if (hasNormal) return "NO_ABNORMALITY";
  return "ABNORMAL";
};

export const correlateSaitamaCsvRows = (baseRows = [], inspectionRows = []) => {
  const inspectionByIdentity = normalizeSaitamaInspectionRows(inspectionRows);
  return dedupeSaitamaCsvRows(baseRows).map((row) => {
    const identity = buildSaitamaCsvIdentity(row);
    const inspection = identity
      ? inspectionByIdentity.get(getSaitamaCsvIdentityKey(identity))
      : null;
    return {
      ...row,
      inspectionResultId: inspection?.id || null,
      inspectionItemCodes: inspection?.itemCodes || [],
      inspectionStatus: classifySaitamaInspection(inspection),
    };
  });
};

export const filterSaitamaCsvRowsByInspection = (rows = [], selectedCodes = []) => {
  if (!Array.isArray(selectedCodes) || selectedCodes.length === 0) return rows;
  return rows.filter((row) => selectedCodes.some((code) => {
    if (code === "UNINSPECTED") return row.inspectionStatus === "UNINSPECTED";
    if (code === "NO_ABNORMALITY") return row.inspectionStatus === "NO_ABNORMALITY";
    return row.inspectionItemCodes.includes(code);
  }));
};

export const filterSaitamaCsvRowsBySelection = (rows = [], selection = []) => {
  if (!Array.isArray(selection) || selection.length === 0) return rows;
  return rows.filter((row) => selection.some((identity) =>
    sameSaitamaCsvIdentity(buildSaitamaCsvIdentity(row), identity)
  ));
};
